import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, ShieldCheck, Truck, TrendingUp, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, stats, products, whyChooseUs } from '../data/mock';
import ProductCard from '../components/ProductCard';

const Home = () => {
  const featuredProducts = products.slice(0, 8);

  return (
    <main>
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-gradient-to-br from-slate-900/10 via-slate-200/70 to-slate-50 overflow-hidden">
        <div className="absolute inset-0 bg-slate-900/10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-200/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-200/40 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
                <span className="w-2 h-2 bg-slate-900 rounded-full animate-pulse" />
                <span className="text-slate-700 text-sm font-medium">Trusted by 1000+ businesses worldwide</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Your Global
                <span className="text-slate-700"> Trading </span>
                Partner
              </h1>

              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                {companyInfo.description}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/products">
                  <Button size="lg" className="bg-slate-900 hover:bg-slate-700 text-white px-8 gap-2">
                    Browse Products
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-slate-300 hover:bg-slate-50 px-8">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow ${
                    index === 0 ? 'lg:translate-y-8' : index === 3 ? 'lg:-translate-y-8' : ''
                  }`}
                >
                  <p className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">{stat.value}</p>
                  <p className="text-slate-600 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why Choose Pinto</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We are committed to delivering excellence in global trading with our proven track record
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-slate-900 transition-colors">
                  <CheckCircle2 className="w-8 h-8 text-slate-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Featured Products</h2>
              <p className="text-slate-600">Explore our diverse range of quality products for your business</p>
            </div>
            <Link to="/products" className="mt-4 md:mt-0">
              <Button variant="outline" className="gap-2">
                View All Products
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Comprehensive Trading Solutions</h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                From sourcing to delivery, we provide end-to-end trading services that help businesses grow and succeed in the global marketplace.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Globe, text: 'Global Network in 50+ Countries' },
                  { icon: ShieldCheck, text: 'Quality Assurance Guaranteed' },
                  { icon: Truck, text: 'Fast & Reliable Logistics' },
                  { icon: TrendingUp, text: 'Competitive Market Pricing' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-slate-700" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/services" className="inline-block mt-8">
                <Button className="bg-slate-900 hover:bg-slate-700 gap-2">
                  Our Services
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Ready to Partner?</h3>
                <p className="text-slate-300 mb-6">
                  Join thousands of businesses that trust Pinto Trading for their global sourcing needs.
                </p>
                <Link to="/contact">
                  <Button variant="secondary" className="bg-white text-slate-900 hover:bg-slate-100">
                    Get Started Today
                  </Button>
                </Link>
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-slate-200/50 rounded-3xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Start Growing Your Business Today
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Contact us to discuss your trading requirements. Our team is ready to help you source quality products at competitive prices.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-slate-100 hover:bg-white text-slate-900 px-8">
                Get in Touch
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8">
                View Catalog
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
